import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SERVICE_KEYS = [
  { number: "01", titleKey: "servicesPreview.s01.title", descKey: "servicesPreview.s01.desc" },
  { number: "02", titleKey: "servicesPreview.s02.title", descKey: "servicesPreview.s02.desc" },
  { number: "03", titleKey: "servicesPreview.s03.title", descKey: "servicesPreview.s03.desc" },
  { number: "04", titleKey: "servicesPreview.s04.title", descKey: "servicesPreview.s04.desc" },
];

export default function ServicesPreview() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-cool-gray-50 py-20 md:py-28" ref={ref}>
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div
          className={`mb-14 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p className="text-xs uppercase tracking-widest text-gold font-medium mb-4">
            {t("servicesPreview.label")}
          </p>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <h2 className="text-3xl md:text-4xl font-bold text-navy leading-snug max-w-lg">
              {t("servicesPreview.title")}
            </h2>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-navy font-semibold text-sm hover:text-gold transition-colors duration-200 cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              {t("servicesPreview.viewAll")}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-base" />
              </span>
            </Link>
          </div>
        </div>

        <hr className="border-cool-gray-200 mb-12" />

        <div className="space-y-0">
          {SERVICE_KEYS.map((service, i) => (
            <div
              key={service.number}
              className={`group flex flex-col md:flex-row md:items-start gap-6 py-8 border-b border-cool-gray-200 transition-all duration-700 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${i * 100 + 100}ms` }}
            >
              <span className="text-4xl font-bold text-cool-gray-200 group-hover:text-gold/30 transition-colors duration-300 leading-none w-16 flex-shrink-0 select-none">
                {service.number}
              </span>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-navy mb-2">{t(service.titleKey)}</h3>
                <p className="text-sm text-navy/60 leading-relaxed">{t(service.descKey)}</p>
              </div>
            </div>
          ))}
        </div>

        <div
          className={`mt-10 transition-all duration-700 delay-500 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <Link
            to="/services"
            className="inline-flex items-center gap-2 px-6 py-3 bg-navy text-white text-sm font-semibold rounded-md hover:bg-navy-light transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            {t("servicesPreview.explore")}
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-base" />
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}
